import {
  buildBusinessPushClickPath,
  isBusinessPushType,
  requestPushNavigate,
} from '../utils/businessPushNavigation';

const ICON_PATH = '/gop_ai_192_192_wht.png';
const DEDUPE_MS = 3000;
const recentTags = new Map();
const DEBUG = process.env.NODE_ENV === 'development';
const DEFAULT_SENDER_NAME = '알림';

function logDebug(...args) {
  if (DEBUG) {
    console.info('[chat-notify]', ...args);
  }
}

/** BusinessPushTextUtil.pushTitleWithCategory 와 동일 — 예) "홍길동 (채팅)" */
export function categoryLabelForPushType(pushType) {
  const type = pushType != null ? String(pushType).trim() : '';
  if (type === 'chat') return '채팅';
  if (type === 'design') return '설계';
  if (type === 'subscription') return '청약';
  return null;
}

function stripCategorySuffix(name) {
  return String(name).replace(/\s+\([^)]+\)\s*$/, '').trim();
}

export function formatPushTitleWithCategory(senderName, categoryLabel) {
  const name = stripCategorySuffix(senderName || '') || DEFAULT_SENDER_NAME;
  if (categoryLabel == null || String(categoryLabel).trim() === '') {
    return name;
  }
  return `${name} (${String(categoryLabel).trim()})`;
}

/** FCM data.title 우선, 없으면 notification.title + 업무 구분 접미사 */
export function resolvePushNotificationTitle({ dataTitle, notificationTitle, pushType }) {
  const raw = [dataTitle, notificationTitle]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find((v) => v.length > 0);
  const label = categoryLabelForPushType(pushType);
  if (!raw) {
    return label ? formatPushTitleWithCategory(DEFAULT_SENDER_NAME, label) : 'GOPAI';
  }
  if (!label) {
    return raw;
  }
  return formatPushTitleWithCategory(raw, label);
}

function displayNotification(title, options) {
  const safeTitle = title || 'GOPAI';
  const clickUrl = options?.data?.url;
  // 포그라운드(탭 visible): 페이지 Notification API가 SW보다 안정적
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    try {
      const notification = new Notification(safeTitle, options);
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (clickUrl) {
          requestPushNavigate(clickUrl);
        }
      };
      logDebug('표시(foreground Notification API)', safeTitle, options?.tag);
      return;
    } catch (e) {
      logDebug('foreground Notification API 실패, SW fallback', e);
    }
  }
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((reg) => reg.showNotification(safeTitle, options))
      .then(() => logDebug('표시(SW showNotification)', safeTitle, options?.tag))
      .catch((e) => {
        logDebug('SW showNotification 실패', e);
        try {
          new Notification(safeTitle, options);
        } catch (err) {
          console.warn('[chat-notify] 알림 표시 실패:', err);
        }
      });
    return;
  }
  try {
    new Notification(safeTitle, options);
  } catch (e) {
    console.warn('[chat-notify] 알림 표시 실패:', e);
  }
}

export function setActiveChatRoomId(roomId) {
  if (roomId != null && roomId !== '') {
    sessionStorage.setItem('chatActiveRoomId', String(roomId));
  } else {
    sessionStorage.removeItem('chatActiveRoomId');
  }
}

export function shouldSuppressChatNotification(roomId) {
  if (document.visibilityState !== 'visible' || window.location.pathname !== '/chat') {
    return false;
  }
  const active = sessionStorage.getItem('chatActiveRoomId');
  return active != null && String(roomId) === active;
}

/** 채팅 등 SPA 이동이 필요한 클릭 경로 */
export function buildWebPushClickPath(pushType, roomId) {
  const type = pushType != null ? String(pushType).trim() : '';
  if (type === 'chat') {
    if (roomId != null && roomId !== '') {
      return `/chat?roomId=${encodeURIComponent(String(roomId))}`;
    }
    return '/chat';
  }
  return '/chat';
}

function buildNotificationTag({ pushType, roomId, messageId, resourceId }) {
  const type = pushType != null ? String(pushType).trim() : 'chat';
  if (type === 'chat') {
    return messageId ? `chat-${messageId}` : `chat-${roomId || 'unknown'}-${Date.now()}`;
  }
  return `${type}-${resourceId || Date.now()}`;
}

export function showChatBrowserNotification({
  title,
  body,
  roomId,
  messageId,
  pushType = 'chat',
  resourceId,
  eventType,
  targetScreen,
}) {
  if (typeof window === 'undefined') {
    return;
  }
  if (Notification.permission !== 'granted') {
    logDebug('스킵: Notification.permission =', Notification.permission);
    return;
  }
  if (roomId != null && shouldSuppressChatNotification(roomId)) {
    logDebug('스킵: 현재 보고 있는 채팅방', roomId);
    return;
  }

  const tag = buildNotificationTag({ pushType, roomId, messageId, resourceId });
  const now = Date.now();
  const lastShown = recentTags.get(tag);
  if (lastShown != null && now - lastShown < DEDUPE_MS) {
    logDebug('스킵: dedupe', tag);
    return;
  }
  recentTags.set(tag, now);

  const notificationData = {
    pushType: pushType != null ? String(pushType) : 'chat',
    roomId: roomId != null ? String(roomId) : '',
  };
  if (isBusinessPushType(pushType)) {
    notificationData.url = buildBusinessPushClickPath({ pushType, eventType, targetScreen });
    notificationData.targetScreen = targetScreen != null ? String(targetScreen) : '';
  } else {
    notificationData.url = buildWebPushClickPath(pushType, roomId);
  }
  const options = {
    body: body || '',
    icon: ICON_PATH,
    tag,
    data: notificationData,
  };
  displayNotification(title, options);
}

/** 업무알림 채팅 미러 — STOMP 브라우저 알림·채팅 FCM 대상 아님(FCM은 업무 푸시만) */
export const SYSTEM_NOTIFICATION_MESSAGE_TYPE = 'system';

export function isSystemNotificationChatMessage(message) {
  const type = message?.messageType != null ? String(message.messageType).trim() : '';
  return type === SYSTEM_NOTIFICATION_MESSAGE_TYPE;
}

export function parseChatStompPayload(messageBody) {
  try {
    const payload = JSON.parse(messageBody);
    if (payload.type === 'MESSAGES_READ' || payload.id == null || payload.senderId == null) {
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

export function notifyIncomingChatMessage(message, currentUserId) {
  if (!message || currentUserId == null) {
    return;
  }
  if (isSystemNotificationChatMessage(message)) {
    logDebug('스킵: 업무알림 채팅 미러(브라우저 알림은 FCM 업무 푸시만)');
    return;
  }
  const senderId = Number(message.senderId);
  const myId = Number(currentUserId);
  if (senderId === myId) {
    return;
  }
  showChatBrowserNotification({
    title: formatPushTitleWithCategory(message.senderName, '채팅'),
    body: message.content || '',
    roomId: message.roomId,
    messageId: message.id,
  });
}
